import React, { useCallback, useMemo, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { operateType as OperateType, platformEntity, entityAction } from '~constants';
import { useDispatch } from 'react-redux';
import PropertyList from '~components/special-offer/map/offer-map-by-property/property-list';
import showElementByAuth from '~helpers/auth';
import PropertyListEditor from '~components/special-offer/map/offer-map-by-property/property-list-editor';
import * as actions from '~actions/special-offer/offer-list';
import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { QUERY_SPECIAL_OFFER_PROPERTIES } from './property-list/queries';

export default function OfferMapByProperty(props) {
  const { offerInfo, operateType, goToNextStep, redirectToList, unLinkSpecialOffer } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [propertyEditorParams, setPropertyEditorParams] = useState();

  const [pagination, setPagination] = useState({
    pageSize: 10,
    pageNumber: 1,
    landlordIds: null,
  });

  const {
    data: propertiesData,
    loading: getPropertiesLoading,
    refetch: refetchPropertiesData,
  } = useQuery(QUERY_SPECIAL_OFFER_PROPERTIES, {
    variables: {
      id: offerInfo.id,
      landlordIds: pagination.landlordIds,
      propertiesPageNum: pagination.pageNumber,
      propertiesPageSize: pagination.pageSize,
    },
    fetchPolicy: 'network-only',
  });

  const handleRequestAdd = useCallback(() => {
    setPropertyEditorParams({
      editType: 'Add',
    });
  }, []);

  const handleClickBack = useCallback(() => {
    redirectToList();
  }, []);

  const handleOnCloseEditor = useCallback(() => {
    setPropertyEditorParams(null);
  }, []);

  const refreshOffer = useCallback(() => {
    dispatch(actions.getOperatingOffer(offerInfo.id));
  }, [offerInfo]);

  const handleOnSuccess = () => {
    refreshOffer();
    refetchPropertiesData();
  };
  const properties = useMemo(
    () => propertiesData?.node?.properties?.edges?.map(e => e.node),
    [propertiesData],
  );

  const showEmptyTip = useMemo(() => {
    return properties?.length === 0;
  }, [properties]);

  const pageInfo = useMemo(
    () => ({
      totalCount: propertiesData?.node?.properties?.totalCount,
      ...propertiesData?.node?.properties?.pageInfo,
    }),
    [propertiesData],
  );

  const handleUnLinkSpecialOffer = useCallback(
    params => {
      unLinkSpecialOffer(params, () => refetchPropertiesData());
    },
    [refetchPropertiesData],
  );

  return (
    <div className="offer-map-by-property">
      <If condition={!showEmptyTip}>
        <div className="offer-map-by-property__header">
          <Button
            className="offer-map-by-property__header__add-property-btn"
            onClick={handleRequestAdd}
          >
            <PlusOutlined />
            {t('cms.table.header.button.add_new_property')}
          </Button>
        </div>
      </If>

      <Choose>
        <When condition={showEmptyTip}>
          <Empty
            className="offer-map-by-property__empty"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span>You don’t have any properties now.</span>}
          >
            <Button onClick={handleRequestAdd} icon="plus">
              {t('cms.table.header.button.add_new_property')}
            </Button>
          </Empty>
        </When>
        <Otherwise>
          <PropertyList
            offerInfo={offerInfo}
            properties={properties}
            unLinkSpecialOffer={handleUnLinkSpecialOffer}
            onClickAddProperty={handleRequestAdd}
            loading={getPropertiesLoading}
            refetch={refetchPropertiesData}
            setPagination={setPagination}
            pagination={pagination}
            pageInfo={pageInfo}
          />
        </Otherwise>
      </Choose>

      <div
        className={classNames('offer-map-by-property__footer', {
          'offer-map-by-property__footer--justify-end': operateType === OperateType.add,
        })}
      >
        <If
          condition={
            operateType === OperateType.edit &&
            showElementByAuth(platformEntity.SPECIAL_OFFERS_SPECIAL_OFFERS, entityAction.UPDATE)
          }
        >
          <Button className="create-offer-content__continue-btn" onClick={handleClickBack}>
            Exit
          </Button>
        </If>
        <If
          condition={
            operateType === OperateType.add &&
            showElementByAuth(platformEntity.SPECIAL_OFFERS_SPECIAL_OFFERS, entityAction.CREATE)
          }
        >
          <Button
            type="primary"
            className="create-offer-content__continue-save-btn"
            onClick={goToNextStep}
          >
            {t('cms.sepcial_offer.save_continue.btn')}
          </Button>
        </If>
      </div>

      <If condition={propertyEditorParams}>
        <PropertyListEditor
          activeModal
          onClose={handleOnCloseEditor}
          onSuccess={handleOnSuccess}
          specialOfferId={offerInfo.id}
        />
      </If>
    </div>
  );
}
